// src/services/apiService.js
import axios from 'axios';
import { toast } from 'react-toastify';

// Axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 10000, // Set timeout for reliability
});

// Request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log(`Request: ${config.method.toUpperCase()} ${config.url}`, config.headers);
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for global error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const message = error.response?.data?.error || error.message;
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      toast.error('Session expired. Please log in again.');
      // Optionally redirect to login page
      // window.location.href = '/login';
    } else if (error.response?.status === 403) {
      toast.error('Access denied.');
    } else {
      toast.error(message || 'An error occurred.');
    }
    return Promise.reject(error);
  }
);

const apiService = {
  // Generic request method
  async request(method, url, data = null, config = {}) {
    try {
      const response = await api({ method, url, data, ...config });
      return response.data;
    } catch (error) {
      throw error.response?.data?.error || error.message;
    }
  },

  // JSON methods
  get(url, config = {}) {
    return this.request('get', url, null, config);
  },

  post(url, data, config = {}) {
    return this.request('post', url, data, config);
  },

  put(url, data, config = {}) {
    return this.request('put', url, data, config);
  },

  delete(url, config = {}) {
    return this.request('delete', url, null, config);
  },

  // Binary data (blob) for PDFs/images
  async getBlob(url, config = {}) {
    try {
      const response = await api.get(url, { ...config, responseType: 'blob' });
      return { blob: response.data, contentType: response.headers['content-type'] };
    } catch (error) {
      throw error.response?.data?.error || error.message;
    }
  },

  // File upload
  async upload(url, formData, config = {}) {
    try {
      const response = await api.post(url, formData, {
        ...config,
        headers: { ...config.headers, 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.error || error.message;
    }
  },
};

export default apiService;